/**
 * Curated equipment library — real gear with calibrated DNA profiles.
 *
 * Modifiers are relative to TrackMan PGA Tour averages (existing club presets).
 * DNA profiles sourced from published launch monitor data (TXG, MGS, Crossfield),
 * manufacturer specs, and USGA equipment databases.
 *
 * Modifier ranges:
 *   ballSpeedMod  0.97 – 1.04  (±3% ball speed)
 *   launchMod    -1.5 – +1.5°  (additive launch angle change)
 *   spinMod       0.80 – 1.15  (±15% spin rate)
 *
 * DNA calibration notes:
 *   - CG positions from manufacturer data and MyGolfSpy disassembly reports
 *   - MOI from USGA conformance database and manufacturer claims
 *   - Face flex from published CT testing and independent robot data
 *   - Shaft profiles from EI profiling services (Club Champion, Cool Clubs)
 *   - Ball layer/dimple from manufacturer specs and independent cuts
 */

import type {
  DriverHead, IronSet, Shaft, GolfBall,
  HeadProfile, ShaftProfile, BallProfile,
} from './types';

// ─── Driver Heads ───────────────────────────────────────────────────────────

const DRIVER_RESPONSE_REF_SPEED = 100;
const DRIVER_RESPONSE_REF_ATTACK = -1.3;
const DRIVER_RESPONSE_MAX_RPM = 220;

export const DRIVERS: DriverHead[] = [
  {
    id: 'avg', name: 'Average', brand: '--', year: 2024,
    loft: 10.5, ballSpeedMod: 1.0, launchMod: 0, spinMod: 1.0,
    head: { cgDepth: 28, cgHeight: 20, moi: 5000, faceFlexMod: 1.0 },
  },

  // ── TaylorMade ──
  {
    id: 'qi35-max', name: 'Qi35 Max', brand: 'TaylorMade', year: 2025,
    loft: 10.5, ballSpeedMod: 1.005, launchMod: -0.1, spinMod: 0.988,
    // TrackMan calibrated: N=5 players (fixed baseline) — speed boost, lower spin
    // Design: 460cc, deep CG, highest MOI in class, carbon crown
    head: { cgDepth: 34, cgHeight: 18, moi: 5800, faceFlexMod: 1.02 },
  },
  {
    id: 'qi35-ls', name: 'Qi35 LS', brand: 'TaylorMade', year: 2025,
    loft: 9.0, ballSpeedMod: 1.008, launchMod: -0.4, spinMod: 0.88,
    // Forward CG, lower spin head for fast swingers
    head: { cgDepth: 22, cgHeight: 21, moi: 4800, faceFlexMod: 1.02 },
  },
  {
    id: 'qi10-max', name: 'Qi10 Max', brand: 'TaylorMade', year: 2024,
    loft: 10.5, ballSpeedMod: 1.005, launchMod: 0.5, spinMod: 0.95,
    head: { cgDepth: 32, cgHeight: 18, moi: 5700, faceFlexMod: 1.01 },
  },
  {
    id: 'qi4d', name: 'Qi4D', brand: 'TaylorMade', year: 2026,
    loft: 10.5, ballSpeedMod: 0.999, launchMod: -0.4, spinMod: 0.989,
    // TrackMan calibrated: N=52 players (fixed baseline) — neutral speed, low spin
    head: { cgDepth: 33, cgHeight: 18, moi: 5600, faceFlexMod: 1.015 },
    spinResponse: { speedSlopeRpmPerMph: -4.0, attackSlopeRpmPerDeg: -8.6, referenceClubSpeed: DRIVER_RESPONSE_REF_SPEED, referenceAttackAngle: DRIVER_RESPONSE_REF_ATTACK, maxAdjustmentRpm: DRIVER_RESPONSE_MAX_RPM },
  },
  {
    id: 'qntm', name: 'Quantum (variant unverified)', brand: 'Callaway', year: 2026,
    loft: 10.5, ballSpeedMod: 0.998, launchMod: -0.2, spinMod: 0.979,
    // TrackMan calibrated: N=41 players (fixed baseline) — neutral speed, low spin
    head: { cgDepth: 35, cgHeight: 17, moi: 5900, faceFlexMod: 1.025 },
    spinResponse: { speedSlopeRpmPerMph: -6.3, attackSlopeRpmPerDeg: -11.5, referenceClubSpeed: DRIVER_RESPONSE_REF_SPEED, referenceAttackAngle: DRIVER_RESPONSE_REF_ATTACK, maxAdjustmentRpm: DRIVER_RESPONSE_MAX_RPM },
  },

  // ── Callaway ──
  {
    id: 'paradym-ai-max', name: 'Paradym Ai Smoke Max', brand: 'Callaway', year: 2024,
    loft: 10.5, ballSpeedMod: 1.01, launchMod: 0.3, spinMod: 0.96,
    // AI-designed face, Jailbreak + carbon
    head: { cgDepth: 30, cgHeight: 19, moi: 5600, faceFlexMod: 1.025 },
  },
  {
    id: 'elyte-max', name: 'Elyte Max', brand: 'Callaway', year: 2025,
    loft: 10.5, ballSpeedMod: 1.012, launchMod: 0.4, spinMod: 0.93,
    // Latest AI face iteration, ultra-low spin head
    head: { cgDepth: 31, cgHeight: 17, moi: 5700, faceFlexMod: 1.03 },
  },

  // ── Titleist ──
  {
    id: 'gt2', name: 'GT2', brand: 'Titleist', year: 2024,
    loft: 9.0, ballSpeedMod: 1.004, launchMod: 0.0, spinMod: 1.005,
    // TrackMan calibrated: N=17 players (fixed baseline) — slight speed boost, near-neutral spin
    head: { cgDepth: 24, cgHeight: 21, moi: 4600, faceFlexMod: 1.01 },
    spinResponse: { speedSlopeRpmPerMph: 8.6, attackSlopeRpmPerDeg: 9.7, referenceClubSpeed: DRIVER_RESPONSE_REF_SPEED, referenceAttackAngle: DRIVER_RESPONSE_REF_ATTACK, maxAdjustmentRpm: DRIVER_RESPONSE_MAX_RPM },
  },
  {
    id: 'gt3', name: 'GT3', brand: 'Titleist', year: 2024,
    loft: 10.5, ballSpeedMod: 0.998, launchMod: 0.4, spinMod: 1.018,
    // TrackMan calibrated: N=17 players (fixed baseline) — higher spin/launch
    head: { cgDepth: 29, cgHeight: 19, moi: 5200, faceFlexMod: 1.01 },
    spinResponse: { speedSlopeRpmPerMph: 1.4, attackSlopeRpmPerDeg: -48.1, referenceClubSpeed: DRIVER_RESPONSE_REF_SPEED, referenceAttackAngle: DRIVER_RESPONSE_REF_ATTACK, maxAdjustmentRpm: DRIVER_RESPONSE_MAX_RPM },
  },
  {
    id: 'gt1', name: 'GT1', brand: 'Titleist', year: 2025,
    loft: 12.0, ballSpeedMod: 1.000, launchMod: 0.2, spinMod: 1.030,
    // Titleist GT1 — high-launch, high-forgiveness. Placeholder, N=1.
    head: { cgDepth: 35, cgHeight: 16, moi: 5800, faceFlexMod: 1.01 },
  },
  {
    id: 'gt4', name: 'GT4', brand: 'Titleist', year: 2024,
    loft: 10.5, ballSpeedMod: 1.007, launchMod: 0.3, spinMod: 0.95,
    // Max forgiveness Titleist
    head: { cgDepth: 30, cgHeight: 18, moi: 5400, faceFlexMod: 1.015 },
  },
  {
    id: 'optm', name: 'OPTM (variant unverified)', brand: 'Cobra', year: 2026,
    loft: 10.5, ballSpeedMod: 0.998, launchMod: 0.2, spinMod: 0.997,
    // TrackMan calibrated: N=10 players (fixed baseline) — near-neutral
    head: { cgDepth: 31, cgHeight: 19, moi: 5500, faceFlexMod: 1.01 },
    spinResponse: { speedSlopeRpmPerMph: 11.9, attackSlopeRpmPerDeg: -8.5, referenceClubSpeed: DRIVER_RESPONSE_REF_SPEED, referenceAttackAngle: DRIVER_RESPONSE_REF_ATTACK, maxAdjustmentRpm: DRIVER_RESPONSE_MAX_RPM },
  },

  // ── Ping ──
  {
    id: 'gle4', name: 'GLe4', brand: 'Ping', year: 2024,
    loft: 12.0, ballSpeedMod: 0.996, launchMod: 0.2, spinMod: 1.000,
    // Ping GLe4 — women's max forgiveness driver. Placeholder, N=1.
    head: { cgDepth: 36, cgHeight: 15, moi: 5900, faceFlexMod: 1.01 },
  },
  {
    id: 'g440-max', name: 'G440 Max', brand: 'Ping', year: 2025,
    loft: 10.5, ballSpeedMod: 1.002, launchMod: 0.2, spinMod: 1.019,
    // TrackMan calibrated: N=29 players (fixed baseline) — higher spin, neutral speed
    head: { cgDepth: 32, cgHeight: 18, moi: 5700, faceFlexMod: 1.01 },
    spinResponse: { speedSlopeRpmPerMph: -5.2, attackSlopeRpmPerDeg: -26.1, referenceClubSpeed: DRIVER_RESPONSE_REF_SPEED, referenceAttackAngle: DRIVER_RESPONSE_REF_ATTACK, maxAdjustmentRpm: DRIVER_RESPONSE_MAX_RPM },
  },
  {
    id: 'g440-lst', name: 'G440 LST', brand: 'Ping', year: 2025,
    loft: 9.0, ballSpeedMod: 1.005, launchMod: -0.4, spinMod: 0.89,
    // Low-spin tour model
    head: { cgDepth: 22, cgHeight: 22, moi: 4500, faceFlexMod: 1.01 },
  },

  // ── Additional fitting aliases (exact configurations remain unverified) ──
  {
    id: 'qntm-td', name: 'Quantum Triple Diamond Max', brand: 'Callaway', year: 2026,
    loft: 10.5, ballSpeedMod: 1.001, launchMod: 0.3, spinMod: 1.007,
    // TrackMan calibrated: N=4 players — neutral speed/spin, slight launch boost
    head: { cgDepth: 34, cgHeight: 18, moi: 5700, faceFlexMod: 1.02 },
  },
  {
    id: 'qi4d-core', name: 'Qi4D Core', brand: 'TaylorMade', year: 2026,
    loft: 10.5, ballSpeedMod: 0.993, launchMod: 1.7, spinMod: 1.058,
    // TrackMan calibrated: N=2 players — draw-biased, higher launch/spin
    head: { cgDepth: 33, cgHeight: 17, moi: 5600, faceFlexMod: 1.015 },
  },
  {
    id: 'qi4d-ls', name: 'Qi4D LS', brand: 'TaylorMade', year: 2026,
    loft: 9.0, ballSpeedMod: 1.006, launchMod: -1.3, spinMod: 0.908,
    // TrackMan calibrated: N=4 players — lowest spin in TM lineup, low launch
    head: { cgDepth: 24, cgHeight: 21, moi: 4900, faceFlexMod: 1.015 },
  },
  {
    id: 'sim2', name: 'SIM2', brand: 'TaylorMade', year: 2021,
    loft: 10.5, ballSpeedMod: 1.008, launchMod: -0.7, spinMod: 1.040,
    // TrackMan calibrated: N=3 players — good ball speed, higher spin, low launch
    head: { cgDepth: 30, cgHeight: 19, moi: 5400, faceFlexMod: 1.01 },
  },

  // ── Titleist (Denver data) ──
  {
    id: 'tsi3', name: 'TSi3', brand: 'Titleist', year: 2021,
    loft: 9.5, ballSpeedMod: 0.989, launchMod: 0.7, spinMod: 1.083,
    // TrackMan calibrated: N=2 players — speed loss, higher spin/launch
    head: { cgDepth: 26, cgHeight: 20, moi: 4800, faceFlexMod: 1.01 },
  },

  // ── Ping (Denver data) ──
  {
    id: 'g440k', name: 'G440 K', brand: 'Ping', year: 2025,
    loft: 10.5, ballSpeedMod: 1.000, launchMod: -0.3, spinMod: 1.080,
    // TrackMan calibrated: N=4 players — highest spin in Ping lineup, neutral speed
    head: { cgDepth: 31, cgHeight: 18, moi: 5600, faceFlexMod: 1.01 },
  },

  // ── Srixon (Denver data) ──
  {
    id: 'zxi', name: 'ZXi', brand: 'Srixon', year: 2025,
    loft: 10.5, ballSpeedMod: 1.008, launchMod: -1.05, spinMod: 0.913,
    // TrackMan calibrated: N=3 players — hot face, low spin/launch
    head: { cgDepth: 29, cgHeight: 19, moi: 5300, faceFlexMod: 1.01 },
  },
  {
    id: 'zxi-ls', name: 'ZXi LS', brand: 'Srixon', year: 2025,
    loft: 9.0, ballSpeedMod: 0.998, launchMod: 0.3, spinMod: 1.028,
    // TrackMan calibrated: N=4 players — counter-intuitively higher spin than ZXi
    head: { cgDepth: 23, cgHeight: 21, moi: 4700, faceFlexMod: 1.01 },
  },

  // ── Mizuno (Denver data) ──
  {
    id: 'jpx-one', name: 'JPX One', brand: 'Mizuno', year: 2025,
    loft: 10.5, ballSpeedMod: 0.986, launchMod: 1.5, spinMod: 1.105,
    // TrackMan calibrated: N=3 players — highest spin, high launch, speed loss
    head: { cgDepth: 30, cgHeight: 19, moi: 5300, faceFlexMod: 1.01 },
  },

  // ── Cobra ──
  {
    id: 'darkspeed-max', name: 'Darkspeed Max', brand: 'Cobra', year: 2025,
    loft: 10.5, ballSpeedMod: 1.005, launchMod: 0.3, spinMod: 0.95,
    head: { cgDepth: 30, cgHeight: 19, moi: 5500, faceFlexMod: 1.01 },
  },
  {
    id: 'darkspeed-ls', name: 'Darkspeed LS', brand: 'Cobra', year: 2025,
    loft: 9.0, ballSpeedMod: 1.005, launchMod: -0.8, spinMod: 0.90,
    head: { cgDepth: 20, cgHeight: 22, moi: 4400, faceFlexMod: 1.01 },
  },

  // ── Mizuno ──
  {
    id: 'st-max-230', name: 'ST-Max 230', brand: 'Mizuno', year: 2024,
    loft: 10.5, ballSpeedMod: 1.003, launchMod: 0.75, spinMod: 0.97,
    head: { cgDepth: 30, cgHeight: 19, moi: 5300, faceFlexMod: 1.005 },
  },

  // ── Srixon ──
  {
    id: 'zx5-mkii', name: 'ZX5 MkII', brand: 'Srixon', year: 2024,
    loft: 10.5, ballSpeedMod: 1.004, launchMod: 0.2, spinMod: 0.96,
    head: { cgDepth: 28, cgHeight: 19, moi: 5200, faceFlexMod: 1.01 },
  },
];

// ─── Iron Sets ──────────────────────────────────────────────────────────────

export const IRON_SETS: IronSet[] = [
  {
    id: 'avg', name: 'Average', brand: '--', year: 2024,
    category: 'players', ballSpeedMod: 1.0, launchMod: 0, spinMod: 1.0,
    head: { cgDepth: 6, cgHeight: 15, moi: 2800, faceFlexMod: 1.0 },
  },

  // ── Blades ──
  {
    id: 't100', name: 'T100', brand: 'Titleist', year: 2025,
    category: 'blade', ballSpeedMod: 0.996, launchMod: 0.4, spinMod: 1.020,
    // TrackMan calibrated: N=7 (T150 + CB24, fixed baseline) — neutral speed, spin boost
    head: { cgDepth: 3, cgHeight: 17, moi: 2200, faceFlexMod: 0.98 },
  },
  {
    id: 'mp-245', name: 'MP-245', brand: 'Mizuno', year: 2025,
    category: 'blade', ballSpeedMod: 1.008, launchMod: -1.0, spinMod: 0.977,
    // TrackMan calibrated: N=8 players (JPX Forged, fixed baseline) — speed boost, low launch/spin
    head: { cgDepth: 3, cgHeight: 16, moi: 2300, faceFlexMod: 0.98 },
  },

  // ── Players ──
  {
    id: 'apex-pro-25', name: 'Apex Pro 25', brand: 'Callaway', year: 2025,
    category: 'players', ballSpeedMod: 1.001, launchMod: -0.3, spinMod: 0.963,
    // TrackMan calibrated: N=15 players (fixed baseline) — neutral speed, lower spin/launch
    head: { cgDepth: 5, cgHeight: 14, moi: 2900, faceFlexMod: 1.02 },
  },
  {
    id: 'i230', name: 'i230', brand: 'Ping', year: 2024,
    category: 'players', ballSpeedMod: 1.01, launchMod: 0, spinMod: 0.98,
    head: { cgDepth: 5, cgHeight: 15, moi: 2800, faceFlexMod: 1.005 },
  },
  {
    id: 'zxir', name: 'ZXiR', brand: 'Srixon', year: 2025,
    category: 'players-distance', ballSpeedMod: 1.012, launchMod: -0.6, spinMod: 0.995,
    // TrackMan calibrated: N=7 players (fixed baseline) — speed boost, lower launch
    head: { cgDepth: 7, cgHeight: 13, moi: 3100, faceFlexMod: 1.02 },
  },
  {
    id: 'zxi5', name: 'ZXi5', brand: 'Srixon', year: 2025,
    category: 'players', ballSpeedMod: 1.008, launchMod: 0.3, spinMod: 1.013,
    // TrackMan calibrated: N=5 players (fixed baseline) — speed boost, slight spin boost
    head: { cgDepth: 5, cgHeight: 14, moi: 2900, faceFlexMod: 1.015 },
  },

  // ── Players Distance ──
  {
    id: 'p790-25', name: 'P790 2025', brand: 'TaylorMade', year: 2025,
    category: 'players-distance', ballSpeedMod: 1.005, launchMod: 0.0, spinMod: 1.002,
    // TrackMan calibrated: N=15 players (fixed baseline) — slight speed boost, near-neutral spin
    head: { cgDepth: 8, cgHeight: 12, moi: 3200, faceFlexMod: 1.025 },
  },
  {
    id: 't250', name: 'T250', brand: 'Titleist', year: 2025,
    category: 'players-distance', ballSpeedMod: 0.986, launchMod: 1.0, spinMod: 1.066,
    // TrackMan calibrated: N=16 players (fixed baseline) — lower speed, higher spin/launch
    head: { cgDepth: 7, cgHeight: 13, moi: 3100, faceFlexMod: 1.02 },
  },
  {
    id: 'i240', name: 'i240', brand: 'Ping', year: 2025,
    category: 'players-distance', ballSpeedMod: 1.03, launchMod: 0.4, spinMod: 0.92,
    head: { cgDepth: 8, cgHeight: 12, moi: 3200, faceFlexMod: 1.02 },
  },
  {
    id: 'i540', name: 'i540', brand: 'Ping', year: 2025,
    category: 'players-distance', ballSpeedMod: 1.000, launchMod: 0.0, spinMod: 1.000,
    // Ping i540 — placeholder, will update from calibration
    head: { cgDepth: 7, cgHeight: 13, moi: 3100, faceFlexMod: 1.02 },
  },

  // ── Game Improvement ──
  {
    id: 'jpx-925-hm', name: 'JPX 925 Hot Metal', brand: 'Mizuno', year: 2025,
    category: 'game-improvement', ballSpeedMod: 1.006, launchMod: 0.4, spinMod: 1.004,
    // TrackMan calibrated: N=36 players (JPX HL + JPX HM, fixed baseline) — speed boost, near-neutral spin
    head: { cgDepth: 10, cgHeight: 11, moi: 3400, faceFlexMod: 1.03 },
  },
  {
    id: 'qi35-iron', name: 'Qi35', brand: 'TaylorMade', year: 2025,
    category: 'game-improvement', ballSpeedMod: 1.04, launchMod: 1.2, spinMod: 0.87,
    head: { cgDepth: 10, cgHeight: 11, moi: 3500, faceFlexMod: 1.03 },
  },
  {
    id: 'zxi7', name: 'ZXi7', brand: 'Srixon', year: 2025,
    category: 'game-improvement', ballSpeedMod: 1.005, launchMod: 0.3, spinMod: 1.01,
    // N=1 player only — defaults based on GI category peers, pending more data
    head: { cgDepth: 10, cgHeight: 11, moi: 3400, faceFlexMod: 1.03 },
  },
  {
    id: 'g440-iron', name: 'G440', brand: 'Ping', year: 2025,
    category: 'game-improvement', ballSpeedMod: 1.007, launchMod: 0.2, spinMod: 1.035,
    // TrackMan calibrated: N=20 players (G440 + G740, fixed baseline) — speed boost, higher spin
    head: { cgDepth: 9, cgHeight: 12, moi: 3300, faceFlexMod: 1.025 },
  },
  {
    id: 'qntm-iron', name: 'Quantum (variant unverified)', brand: 'Callaway', year: 2026,
    category: 'players-distance', ballSpeedMod: 0.978, launchMod: -0.2, spinMod: 0.965,
    // TrackMan calibrated: N=26 players (fixed baseline) — lower speed/spin
    head: { cgDepth: 8, cgHeight: 12, moi: 3200, faceFlexMod: 1.02 },
  },
  {
    id: 't350', name: 'T350', brand: 'Titleist', year: 2025,
    category: 'game-improvement', ballSpeedMod: 1.011, launchMod: -0.7, spinMod: 0.949,
    // TrackMan calibrated: N=7 players (fixed baseline) — speed boost, low spin
    head: { cgDepth: 10, cgHeight: 11, moi: 3500, faceFlexMod: 1.03 },
  },
  {
    id: 'qi-iron', name: 'Qi', brand: 'TaylorMade', year: 2024,
    category: 'game-improvement', ballSpeedMod: 0.997, launchMod: 0.3, spinMod: 1.011,
    // TrackMan calibrated: N=16 players (Qi + QiHL + Max, fixed baseline) — near-neutral speed, slight spin boost
    head: { cgDepth: 11, cgHeight: 10, moi: 3600, faceFlexMod: 1.01 },
  },
  {
    id: 'gle4-iron', name: 'GLe4', brand: 'Ping', year: 2024,
    category: 'game-improvement', ballSpeedMod: 1.004, launchMod: 1.9, spinMod: 1.030,
    // TrackMan calibrated: N=4 players (fixed baseline) — near-neutral speed, high launch and spin
    head: { cgDepth: 11, cgHeight: 10, moi: 3600, faceFlexMod: 1.01 },
  },
];

// ─── Shafts ─────────────────────────────────────────────────────────────────

export const SHAFTS: Shaft[] = [
  {
    id: 'avg', name: 'Average', brand: '--',
    flex: 'S', weight: 65, length: 45.5, material: 'graphite',
    launchMod: 0, spinMod: 1.0,
    profile: { kickPoint: 'mid', torque: 3.5, tipStiffness: 1.0, buttStiffness: 1.0 },
  },

  // ── Low-Spin / Tour Profiles ──────────────────────────────────────────────

  {
    id: 'ventus-black-6', name: 'Ventus Black 6', brand: 'Fujikura',
    flex: '6.0', weight: 63, length: 45.5, material: 'graphite',
    launchMod: -0.8, spinMod: 0.93,
    // Very stiff tip, low torque — the benchmark "low spin" shaft
    profile: { kickPoint: 'high', torque: 2.8, tipStiffness: 1.18, buttStiffness: 1.05 },
  },
  {
    id: 'hzrdus-black-6', name: 'HZRDUS Black 6.0', brand: 'Project X',
    flex: '6.0', weight: 63, length: 45.5, material: 'graphite',
    launchMod: -0.7, spinMod: 0.95,
    // Stiff tip + stiff butt = board-like, lowest torque in class
    profile: { kickPoint: 'high', torque: 2.5, tipStiffness: 1.15, buttStiffness: 1.12 },
  },
  {
    id: 'tensei-1k-black-65', name: 'Tensei 1K Black 65', brand: 'Mitsubishi',
    flex: 'S', weight: 67, length: 45.5, material: 'graphite',
    launchMod: -0.4, spinMod: 0.96,
    // Stiff tip, moderate butt — smooth but low-spin
    profile: { kickPoint: 'mid-high', torque: 3.1, tipStiffness: 1.10, buttStiffness: 1.0 },
  },
  {
    id: 'smoke-rdx-60', name: 'HZRDUS Smoke RDX 60', brand: 'Project X',
    flex: '6.0', weight: 60, length: 45.75, material: 'graphite',
    launchMod: -0.5, spinMod: 0.94,
    // Lightweight low-spin — counter-balanced with heavy tip
    profile: { kickPoint: 'high', torque: 3.0, tipStiffness: 1.15, buttStiffness: 0.95 },
  },

  // ── Mid-Launch / Versatile Profiles ──────────────────────────────────────

  {
    id: 'tensei-av-blue-65', name: 'Tensei AV Blue 65', brand: 'Mitsubishi',
    flex: 'S', weight: 65, length: 45.5, material: 'graphite',
    launchMod: 0, spinMod: 0.98,
    // The do-everything shaft: mid kick, moderate everything
    profile: { kickPoint: 'mid', torque: 3.3, tipStiffness: 1.0, buttStiffness: 1.0 },
  },
  {
    id: 'kai-li-white-60', name: "Kai'li White 60", brand: 'Mitsubishi',
    flex: 'S', weight: 62, length: 45.75, material: 'graphite',
    launchMod: 0.2, spinMod: 0.98,
    // Smooth feel, soft butt for easy loading
    profile: { kickPoint: 'mid', torque: 3.6, tipStiffness: 1.0, buttStiffness: 0.90 },
  },
  {
    id: 'ventus-tr-blue-6', name: 'Ventus TR Blue 6', brand: 'Fujikura',
    flex: '6.0', weight: 64, length: 45.5, material: 'graphite',
    launchMod: -0.2, spinMod: 0.97,
    // Balanced EI profile, good for wide range of swings
    profile: { kickPoint: 'mid', torque: 3.2, tipStiffness: 1.05, buttStiffness: 1.02 },
  },

  // ── High-Launch / Spin Profiles ──────────────────────────────────────────

  {
    id: 'ventus-red-5', name: 'Ventus Red 5', brand: 'Fujikura',
    flex: 'R', weight: 55, length: 46.0, material: 'graphite',
    launchMod: 0.8, spinMod: 1.03,
    // Soft tip, high torque — max launch for moderate swing speeds
    profile: { kickPoint: 'low', torque: 4.8, tipStiffness: 0.85, buttStiffness: 0.90 },
  },
  {
    id: 'speeder-nx-50', name: 'Speeder NX 50', brand: 'Fujikura',
    flex: 'R', weight: 52, length: 46.0, material: 'graphite',
    launchMod: 1.0, spinMod: 1.05,
    // Ultra-light, soft tip — senior/ladies speed boost
    profile: { kickPoint: 'low', torque: 5.5, tipStiffness: 0.82, buttStiffness: 0.88 },
  },
  {
    id: 'autoflex-505x', name: 'AutoFlex 505X', brand: 'AutoFlex',
    flex: 'X', weight: 50, length: 46.0, material: 'graphite',
    launchMod: 0.5, spinMod: 1.0,
    // Ultra-soft butt for max load, stiff tip for low spin — unique profile
    profile: { kickPoint: 'mid', torque: 4.2, tipStiffness: 1.05, buttStiffness: 0.75 },
  },

  // ── Steel Iron Shafts ────────────────────────────────────────────────────

  {
    id: 'dg-s300', name: 'Dynamic Gold S300', brand: 'True Temper',
    flex: 'S', weight: 130, length: 37.0, material: 'steel',
    launchMod: -0.5, spinMod: 1.02,
    // The tour standard. Stiff everything, lowest torque in steel
    profile: { kickPoint: 'high', torque: 1.8, tipStiffness: 1.15, buttStiffness: 1.10 },
  },
  {
    id: 'dg-x100', name: 'Dynamic Gold X100', brand: 'True Temper',
    flex: 'X', weight: 130, length: 37.0, material: 'steel',
    launchMod: -0.8, spinMod: 1.0,
    // Tour extra stiff — for 120+ mph driver speed
    profile: { kickPoint: 'high', torque: 1.5, tipStiffness: 1.20, buttStiffness: 1.15 },
  },
  {
    id: 'kbs-tour-120', name: 'KBS Tour 120', brand: 'KBS',
    flex: 'S', weight: 120, length: 37.0, material: 'steel',
    launchMod: 0, spinMod: 1.0,
    // Slightly lighter than DG, mid-high kick
    profile: { kickPoint: 'mid-high', torque: 2.0, tipStiffness: 1.05, buttStiffness: 1.05 },
  },
  {
    id: 'modus-120', name: 'N.S. Pro Modus 120', brand: 'Nippon',
    flex: 'S', weight: 120, length: 37.0, material: 'steel',
    launchMod: -0.3, spinMod: 0.98,
    // Low spin steel — popular replacement for DG
    profile: { kickPoint: 'mid-high', torque: 1.9, tipStiffness: 1.10, buttStiffness: 1.02 },
  },
  {
    id: 'kbs-tour-lite-105', name: 'KBS Tour Lite 105', brand: 'KBS',
    flex: 'R', weight: 105, length: 37.0, material: 'steel',
    launchMod: 0.3, spinMod: 1.02,
    // Lightweight steel — bridges graphite and steel
    profile: { kickPoint: 'mid', torque: 2.4, tipStiffness: 0.95, buttStiffness: 0.95 },
  },
  {
    id: 'dg-mid-115', name: 'Dynamic Gold Mid 115', brand: 'True Temper',
    flex: 'S', weight: 115, length: 37.0, material: 'steel',
    launchMod: 0, spinMod: 1.0,
    // Mid-weight DG — same feel, less strain
    profile: { kickPoint: 'mid-high', torque: 2.0, tipStiffness: 1.08, buttStiffness: 1.05 },
  },
];

// ─── Golf Balls ─────────────────────────────────────────────────────────────

export const GOLF_BALLS: GolfBall[] = [
  {
    id: 'avg', name: 'Average', brand: '--',
    compression: 90, cover: 'urethane',
    speedMod: 1.0, driverSpinMod: 1.0, ironSpinMod: 1.0, wedgeSpinMod: 1.0,
    ball: { layers: 3, dimpleProfile: 'mid' },
  },

  // ── Premium Tour (Urethane, 3-5 piece) ────────────────────────────────────

  {
    id: 'prov1', name: 'Pro V1', brand: 'Titleist',
    compression: 87, cover: 'urethane',
    speedMod: 1.0, driverSpinMod: 1.0, ironSpinMod: 1.0, wedgeSpinMod: 1.05,
    // 3-piece, 388 dimple, mid flight
    ball: { layers: 3, dimpleProfile: 'mid' },
  },
  {
    id: 'prov1x', name: 'Pro V1x', brand: 'Titleist',
    compression: 100, cover: 'urethane',
    speedMod: 1.005, driverSpinMod: 0.95, ironSpinMod: 0.98, wedgeSpinMod: 1.02,
    // 4-piece, 348 dimple, higher flight, more spin separation
    ball: { layers: 4, dimpleProfile: 'high-flight' },
  },
  {
    id: 'prov1x-left', name: 'Pro V1x Left Dot', brand: 'Titleist',
    compression: 100, cover: 'urethane',
    speedMod: 1.005, driverSpinMod: 0.92, ironSpinMod: 0.96, wedgeSpinMod: 1.0,
    // 4-piece, lower spin version — firmer feel
    ball: { layers: 4, dimpleProfile: 'penetrating' },
  },
  {
    id: 'chrome-soft', name: 'Chrome Soft', brand: 'Callaway',
    compression: 75, cover: 'urethane',
    speedMod: 0.995, driverSpinMod: 1.03, ironSpinMod: 1.02, wedgeSpinMod: 1.08,
    // 4-piece, Graphene core, softest tour ball
    ball: { layers: 4, dimpleProfile: 'high-flight' },
  },
  {
    id: 'chrome-soft-x', name: 'Chrome Soft X', brand: 'Callaway',
    compression: 100, cover: 'urethane',
    speedMod: 1.005, driverSpinMod: 0.95, ironSpinMod: 0.97, wedgeSpinMod: 1.04,
    // Firmer Chrome Soft — more speed, less driver spin
    ball: { layers: 4, dimpleProfile: 'mid' },
  },
  {
    id: 'tp5x', name: 'TP5x', brand: 'TaylorMade',
    compression: 97, cover: 'urethane',
    speedMod: 1.01, driverSpinMod: 0.95, ironSpinMod: 0.97, wedgeSpinMod: 1.0,
    // 5-piece, maximum spin separation, speed-oriented
    ball: { layers: 5, dimpleProfile: 'penetrating' },
  },
  {
    id: 'tp5', name: 'TP5', brand: 'TaylorMade',
    compression: 85, cover: 'urethane',
    speedMod: 1.0, driverSpinMod: 1.0, ironSpinMod: 1.0, wedgeSpinMod: 1.06,
    // 5-piece, softer version — more wedge spin
    ball: { layers: 5, dimpleProfile: 'mid' },
  },
  {
    id: 'tour-bx', name: 'Tour B X', brand: 'Bridgestone',
    compression: 90, cover: 'urethane',
    speedMod: 1.005, driverSpinMod: 0.97, ironSpinMod: 0.99, wedgeSpinMod: 1.02,
    // 3-piece, REACTIV cover, Tiger's ball
    ball: { layers: 3, dimpleProfile: 'mid' },
  },
  {
    id: 'tour-bxs', name: 'Tour B XS', brand: 'Bridgestone',
    compression: 83, cover: 'urethane',
    speedMod: 1.0, driverSpinMod: 0.99, ironSpinMod: 1.0, wedgeSpinMod: 1.06,
    // Softer version — more greenside spin
    ball: { layers: 3, dimpleProfile: 'mid' },
  },
  {
    id: 'z-star-xv', name: 'Z-Star XV', brand: 'Srixon',
    compression: 102, cover: 'urethane',
    speedMod: 1.005, driverSpinMod: 0.94, ironSpinMod: 0.97, wedgeSpinMod: 1.0,
    // 4-piece, firm core — max speed, low driver spin
    ball: { layers: 4, dimpleProfile: 'penetrating' },
  },

  // ── Distance / Value (Ionomer/Surlyn, 2-3 piece) ─────────────────────────

  {
    id: 'velocity', name: 'Velocity', brand: 'Titleist',
    compression: 65, cover: 'ionomer',
    speedMod: 1.01, driverSpinMod: 0.88, ironSpinMod: 0.90, wedgeSpinMod: 0.80,
    // 2-piece, high-speed core, max distance
    ball: { layers: 2, dimpleProfile: 'penetrating' },
  },
  {
    id: 'supersoft', name: 'Supersoft', brand: 'Callaway',
    compression: 38, cover: 'ionomer',
    speedMod: 0.99, driverSpinMod: 0.90, ironSpinMod: 0.92, wedgeSpinMod: 0.82,
    // 2-piece, ultra-soft, max distance for slow swingers
    ball: { layers: 2, dimpleProfile: 'high-flight' },
  },
  {
    id: 'soft-feel', name: 'Soft Feel', brand: 'Srixon',
    compression: 60, cover: 'ionomer',
    speedMod: 1.0, driverSpinMod: 0.91, ironSpinMod: 0.93, wedgeSpinMod: 0.83,
    ball: { layers: 2, dimpleProfile: 'mid' },
  },
  {
    id: 'distance-plus', name: 'Distance+', brand: 'TaylorMade',
    compression: 77, cover: 'ionomer',
    speedMod: 1.01, driverSpinMod: 0.87, ironSpinMod: 0.89, wedgeSpinMod: 0.78,
    // 2-piece, firmest distance ball — max speed
    ball: { layers: 2, dimpleProfile: 'penetrating' },
  },
];

// ─── Lookup helpers ─────────────────────────────────────────────────────────

export function findDriver(id: string): DriverHead {
  return DRIVERS.find(d => d.id === id) ?? DRIVERS[0];
}

export function findIronSet(id: string): IronSet {
  return IRON_SETS.find(s => s.id === id) ?? IRON_SETS[0];
}

export function findShaft(id: string): Shaft {
  return SHAFTS.find(s => s.id === id) ?? SHAFTS[0];
}

export function findBall(id: string): GolfBall {
  return GOLF_BALLS.find(b => b.id === id) ?? GOLF_BALLS[0];
}

// ─── Default Head/Shaft/Ball profiles (for generic specs that lack DNA) ────

export const DEFAULT_HEAD_PROFILE: HeadProfile = {
  cgDepth: 28, cgHeight: 20, moi: 5000, faceFlexMod: 1.0,
};

export const DEFAULT_IRON_HEAD_PROFILE: HeadProfile = {
  cgDepth: 6, cgHeight: 15, moi: 2800, faceFlexMod: 1.0,
};

export const DEFAULT_SHAFT_PROFILE: ShaftProfile = {
  kickPoint: 'mid', torque: 3.5, tipStiffness: 1.0, buttStiffness: 1.0,
};

export const DEFAULT_BALL_PROFILE: BallProfile = {
  layers: 3, dimpleProfile: 'mid',
};
